import React, { useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import BlogHero from "./BlogHero";
import BlogFaq from "./BlogFaq";
import BlogToc from "./BlogToc";
import BlogSummary from "./BlogSummary";
import BlogShare from "./BlogShare";
import BlogBreadcrumbs, { breadcrumbSchema } from "./BlogBreadcrumbs";
import { BlogCtaCard, BlogRelated } from "./BlogSidebarExtras";
import { prepareBlogContent } from "../lib/blogContent";
import {
  CalendarDays,
  Clock3,
  ArrowLeft,
  User2,
} from "lucide-react";
// Used when a post has no featured image set in wp-admin; wpPosts.json
// records null rather than inventing a URL. Same placeholder as BlogCardsRow.
import placeholderImage from "../assets/solarservice.jpeg";
import wpPosts from "../data/wpPosts.json";
import { fallbackBlogBySlug } from "../data/blogFallback";
import slugRedirects from "../data/slugRedirects";

// Reads the post baked into the bundle by `npm run fetch:posts` -- no fetch,
// no loading state, no dependency on blog.savorka.in being up or fast. See
// scripts/fetch-posts.mjs and the note in BlogPage.jsx.
const BlogDetail = () => {
  const { slug } = useParams();
  const blog =
    wpPosts.find((post) => post.slug === slug) || fallbackBlogBySlug(slug);

  // Parses the post's HTML, so it is memoised -- and it sits above the early
  // returns below because hooks cannot run conditionally. It tolerates a
  // missing post for the same reason.
  const { articleHtml, faqs, headings, summaryHtml } = useMemo(
    () => prepareBlogContent(blog?.contentHtml || "", blog?.shortDescription || ""),
    [blog]
  );

  if (!blog && slugRedirects[slug]) {
    return <Navigate to={`/blogs/${slugRedirects[slug]}`} replace />;
  }

  if (!blog) {
    return (
      <section className="min-h-screen bg-[#f8faf8] flex items-center justify-center px-4">
        {/* /blogs/anything-invalid matches a real route, so it never reaches
            the site's 404 and is served as a 200 with this card. Without a
            noindex that is a soft 404: Google indexes the error page under
            whatever slug was guessed. */}
        <Helmet>
          <title>Article not found | Savorka Solar Blog</title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>

        <div className="bg-white max-w-[620px] w-full rounded-3xl border border-[#e5e7eb] shadow-sm p-8 text-center">
          <h2 className="text-[30px] font-bold text-[#111827] mb-3">
            Blog Not Found
          </h2>

          <p className="text-[#6b7280] text-[16px] leading-7 mb-7">
            The article you’re looking for may have been moved or no longer
            exists.
          </p>

          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 rounded-full bg-[#117816] px-6 py-3 text-white font-semibold hover:opacity-90 transition"
          >
            <ArrowLeft size={18} />
            Back to Blogs
          </Link>
        </div>
      </section>
    );
  }

  const canonicalUrl = `https://www.savorka.in/blogs/${blog.slug}/`;
  // Baked images are site-relative ("/blog-images/..."), and so are the
  // bundled ones webpack emits. Open Graph, Twitter and schema.org all
  // require absolute URLs, or the preview image silently fails to load when
  // the page is shared.
  const heroImage = blog.image || placeholderImage;
  // Share cards are rendered around 1200px wide, so the 1024px hero was
  // being upscaled and arrived visibly soft. ogImage is a 1200px rendition
  // kept only for crawlers -- no page visitor ever downloads it -- and falls
  // back to the hero for any post whose source was too small to make one.
  const socialImage = blog.ogImage || heroImage;
  const absoluteImage = socialImage.startsWith("http")
    ? socialImage
    : `https://www.savorka.in${socialImage}`;

  return (
    <div className="bg-[#f7faf7] min-h-screen">
      <Helmet>
        <title>{`${blog.title} | Savorka Solar Blog`}</title>
        <meta name="description" content={blog.metaDescription} />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="article" />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.metaDescription} />
        <meta property="og:image" content={absoluteImage} />
        <meta property="og:url" content={canonicalUrl} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={blog.metaDescription} />
        <meta name="twitter:image" content={absoluteImage} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: blog.title,
            description: blog.metaDescription,
            image: absoluteImage,
            datePublished: blog.dateIso,
            author: {
              "@type": "Person",
              name: blog.author,
            },
            publisher: {
              "@type": "Organization",
              name: "Savorka",
              logo: {
                "@type": "ImageObject",
                url: "https://www.savorka.in/logo.png",
              },
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": canonicalUrl,
            },
          })}
        </script>

        {/* Unlike FAQPage, breadcrumbs are still shown in search results, and
            this mirrors exactly the trail rendered on the page. */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema(blog.title, canonicalUrl))}
        </script>

        {/* Only emitted for posts that actually show an FAQ, and built from
            the same text the accordion renders -- marking up answers a
            visitor cannot see is what the guidelines are there to stop.
            Note Google narrowed FAQ rich results to government and health
            sites in 2023, so this is for correctness and for answer engines
            that read structured data, not for a rich snippet. */}
        {faqs.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqs.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: faq.answerText,
                },
              })),
            })}
          </script>
        )}
      </Helmet>

      {/* Was HeroImageSection, which crops to a fixed band and clips a wave
          out of the bottom -- fine for a decorative page banner, fatal for a
          designed graphic whose headline and icon row run to the edges. */}
      <BlogHero
        image={heroImage}
        alt={blog.title}
        width={blog.imageWidth}
        height={blog.imageHeight}
      />

      {/* Title, article and sidebar share one grid so the breadcrumb, the
          heading and the prose all hang off the same left edge. */}
      <section className="px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <div className="max-w-[1180px] mx-auto lg:grid lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-10">
          {/* First in the DOM so the contents list reads before the article
              on mobile; moved to the right column on desktop. */}
          <div className="lg:col-start-2 lg:row-start-1 lg:flex lg:flex-col">
            <BlogToc headings={headings} />
            {/* mt-auto drops these to the foot of a column that is as tall as
                the article, so they come into view exactly as the reader
                reaches the FAQ rather than sitting unread at the top. */}
            <div className="hidden lg:block lg:mt-auto lg:space-y-6 lg:pt-6">
              <BlogCtaCard />
              <BlogRelated currentSlug={blog.slug} />
            </div>
          </div>

          <div className="min-w-0 lg:col-start-1 lg:row-start-1">
            <BlogBreadcrumbs title={blog.title} />

            <span className="inline-flex rounded-full bg-[#e8f7ea] px-4 py-2 text-[12px] tracking-[0.14em] uppercase font-bold text-[#117816] mb-5">
              {blog.category}
            </span>

            <h1 className="text-[28px] sm:text-[36px] lg:text-[44px] leading-[1.15] font-bold text-[#111827] mb-6">
              {blog.title}
            </h1>

            <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4 border-b border-[#eef2ee] pb-6 mb-8">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[#6b7280] text-[14px]">
                <span className="inline-flex items-center gap-2">
                  <User2 size={16} />
                  {blog.author}
                </span>
                <span className="inline-flex items-center gap-2">
                  <CalendarDays size={16} />
                  {blog.date}
                </span>
                <span className="inline-flex items-center gap-2">
                  <Clock3 size={16} />
                  {blog.readTime}
                </span>
              </div>

              <BlogShare url={canonicalUrl} title={blog.title} />
            </div>

            <BlogSummary html={summaryHtml} />

            <article className="bg-white rounded-[28px] border border-[#edf1ed] shadow-sm p-6 sm:p-10 md:p-14">
              <div
                className="wp-content"
                dangerouslySetInnerHTML={{ __html: articleHtml }}
              />
            </article>

            {/* Lifted out of the article body: in the WordPress HTML the Q&A
                is a run of headings and paragraphs styled like everything
                else, so it read as a footnote rather than a section. */}
            <BlogFaq faqs={faqs} />

            {/* Below the article on mobile, where the sidebar column does not
                exist. */}
            <div className="mt-8 space-y-6 lg:hidden">
              <BlogCtaCard />
              <BlogRelated currentSlug={blog.slug} />
            </div>
          </div>
        </div>
      </section>

      <div className="h-10 md:h-16" />
    </div>
  );
};

export default BlogDetail;
